// src/features/dashboard/components/EmployeeActiveHoursReport/MeVsTeamActiveHours.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import {
  MeVsTeamResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

interface Props {
  data: MeVsTeamResponse;
}

/* -------------------------------------------------------------------------- */
/*                              CONSTANTS                                     */
/* -------------------------------------------------------------------------- */

const MIN_VISIBLE_POINTS = 3;

/* -------------------------------------------------------------------------- */
/*                         DYNAMIC Y-AXIS HELPERS                             */
/* -------------------------------------------------------------------------- */

const getNiceYAxisMax = (maxValue: number): number => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return 1;
  }

  const paddedMax = maxValue * 1.1;

  if (paddedMax <= 5) {
    return Math.ceil(paddedMax);
  }

  if (paddedMax <= 10) {
    return Math.ceil(paddedMax / 2) * 2;
  }

  if (paddedMax <= 20) {
    return Math.ceil(paddedMax / 2) * 2;
  }

  if (paddedMax <= 50) {
    return Math.ceil(paddedMax / 5) * 5;
  }

  if (paddedMax <= 100) {
    return Math.ceil(paddedMax / 10) * 10;
  }

  return Math.ceil(paddedMax / 20) * 20;
};

/* -------------------------------------------------------------------------- */
/*                              Y AXIS TICKS                                  */
/* -------------------------------------------------------------------------- */

const getYAxisTicks = (maxValue: number): number[] => {
  if (maxValue <= 1) {
    return [0, 1];
  }

  if (maxValue <= 5) {
    return Array.from(
      {
        length: maxValue + 1,
      },
      (_, index) => index,
    );
  }

  let step = 2;

  if (maxValue > 20 && maxValue <= 50) {
    step = 5;
  }

  if (maxValue > 50 && maxValue <= 100) {
    step = 10;
  }

  if (maxValue > 100) {
    step = 20;
  }

  const ticks: number[] = [];

  for (
    let value = 0;
    value <= maxValue;
    value += step
  ) {
    ticks.push(value);
  }

  if (ticks[ticks.length - 1] !== maxValue) {
    ticks.push(maxValue);
  }

  return ticks;
};

/* -------------------------------------------------------------------------- */
/*                         DATE LABEL HELPER                                  */
/* -------------------------------------------------------------------------- */

/**
 * Converts:
 *
 * dayName  = "Monday"
 * workDate = "2026-08-17T00:00:00"
 *
 * into:
 *
 * "Mon 17"
 */
const getDayDateLabel = (
  dayName: string,
  workDate: string,
): string => {
  const shortDay =
    dayName?.substring(0, 3) ?? '';

  if (!workDate) {
    return shortDay;
  }

  const datePart =
    workDate.split('T')[0];

  const dateParts =
    datePart.split('-');

  const dayNumber =
    dateParts.length === 3
      ? dateParts[2]
      : '';

  if (!shortDay && !dayNumber) {
    return '';
  }

  if (!dayNumber) {
    return shortDay;
  }

  if (!shortDay) {
    return dayNumber;
  }

  return `${shortDay} ${dayNumber}`;
};

/* -------------------------------------------------------------------------- */
/*                         CUSTOM X AXIS TICK                                 */
/* -------------------------------------------------------------------------- */

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string;
    index?: number;
  };
  activeIndex?: number | null;
}

const CustomXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  activeIndex,
}: CustomXAxisTickProps) => {
  if (!payload) {
    return null;
  }

  const value =
    payload.value ?? '';

  const index =
    payload.index ?? -1;

  const isActive =
    activeIndex !== null &&
    activeIndex !== undefined &&
    index === activeIndex;

  return (
    <g
      transform={`translate(${x},${y})`}
      pointerEvents="none"
      style={{
        overflow: 'visible',
      }}
    >
      {isActive ? (
        <>
          {/* ============================================================
              HOVER POINTER

              The pointer is attached directly to the
              top-center of the selected day box.
          ============================================================ */}

          <path
            d="
              M -7 6
              L 0 -1
              L 7 6
              Z
            "
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* ============================================================
              WHITE OVERLAP

              Removes the bottom edge of the triangle where
              it joins the box, giving the exact connected shape.
          ============================================================ */}

          <rect
            x="-8"
            y="4"
            width="16"
            height="5"
            fill="#FFFFFF"
          />

          {/* ============================================================
              SELECTED DAY BOX

              Same compact design as the supplied image.
          ============================================================ */}

          <rect
            x="-23"
            y="6"
            width="46"
            height="38"
            rx="2"
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          {/* ============================================================
              DAY TEXT
          ============================================================ */}

          <text
            x="0"
            y="27"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#344054"
            fontSize={10}
            fontWeight={400}
          >
            {value
              .substring(0, 3)
              .toUpperCase()}
          </text>
        </>
      ) : (
        /* ==============================================================
           NORMAL X AXIS LABEL
        ============================================================== */

        <text
          x="0"
          y="20"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#475569"
          fontSize={11}
          fontWeight={400}
        >
          {value}
        </text>
      )}
    </g>
  );
};

/* -------------------------------------------------------------------------- */
/*                              TOOLTIP                                       */
/* -------------------------------------------------------------------------- */

const CustomTooltip = ({
  active,
  payload,
  label,
}: any) => {
  if (
    !active ||
    !payload?.length
  ) {
    return null;
  }

  const me = payload.find(
    (item: any) =>
      item.dataKey === 'me',
  );

  const teamAvg = payload.find(
    (item: any) =>
      item.dataKey === 'teamAvg',
  );

  const teamBest = payload.find(
    (item: any) =>
      item.dataKey === 'teamBest',
  );

  return (
    <div
      className="
        min-w-[190px]
        overflow-hidden
        rounded-xl
        border
        border-[#E5E7EB]
        bg-white
        shadow-xl
      "
    >
      {/* Header */}

      <div
        className="
          border-b
          border-[#EEF2F7]
          bg-[#F8FAFC]
          px-3
          py-2
        "
      >
        <span
          className="
            text-[12px]
            font-semibold
            text-[#0F172A]
          "
        >
          {label}
        </span>
      </div>

      {/* Body */}

      <div className="space-y-2 px-3 py-2">

        {/* Me */}

        <div
          className="
            flex
            items-center
            justify-between
            gap-3
          "
        >
          <div
            className="
              flex
              items-center
              gap-2
            "
          >
            <span
              className="
                h-2.5
                w-2.5
                rounded-full
                bg-[#7C4DDF]
              "
            />

            <span
              className="
                text-[12px]
                text-[#334155]
              "
            >
              Me
            </span>
          </div>

          <span
            className="
              text-[12px]
              font-semibold
              text-[#111827]
            "
          >
            {me?.value != null
              ? `${Number(
                  me.value,
                ).toFixed(2)}h`
              : '--'}
          </span>
        </div>

        {/* Team Avg */}

        <div
          className="
            flex
            items-center
            justify-between
            gap-3
          "
        >
          <div
            className="
              flex
              items-center
              gap-2
            "
          >
            <span
              className="
                h-2.5
                w-2.5
                rounded-full
                bg-[#94A3B8]
              "
            />

            <span
              className="
                text-[12px]
                text-[#334155]
              "
            >
              Team Avg
            </span>
          </div>

          <span
            className="
              text-[12px]
              font-semibold
              text-[#111827]
            "
          >
            {teamAvg?.value != null
              ? `${Number(
                  teamAvg.value,
                ).toFixed(2)}h`
              : '--'}
          </span>
        </div>

        {/* Team Best */}

        <div
          className="
            flex
            items-center
            justify-between
            gap-3
          "
        >
          <div
            className="
              flex
              items-center
              gap-2
            "
          >
            <span
              className="
                h-2.5
                w-2.5
                rounded-full
                bg-[#22C55E]
              "
            />

            <span
              className="
                text-[12px]
                text-[#334155]
              "
            >
              Team Best
            </span>
          </div>

          <span
            className="
              text-[12px]
              font-semibold
              text-[#111827]
            "
          >
            {teamBest?.value != null
              ? `${Number(
                  teamBest.value,
                ).toFixed(2)}h`
              : '--'}
          </span>
        </div>

      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                         MAIN COMPONENT                                     */
/* -------------------------------------------------------------------------- */

const MeVsTeamActiveHours = ({
  data,
}: Props) => {

  /* ------------------------------------------------------------------------ */
  /*                              REFS                                        */
  /* ------------------------------------------------------------------------ */

  const chartWheelRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  /* ------------------------------------------------------------------------ */
  /*                              STATE                                       */
  /* ------------------------------------------------------------------------ */

  const [activeLine, setActiveLine] =
    useState<
      'me' |
      'teamAvg' |
      'teamBest' |
      null
    >(null);

  /* ------------------------------------------------------------------------ */
  /*                         ACTIVE X-AXIS POINT                              */
  /* ------------------------------------------------------------------------ */

  const [
    activeXAxisIndex,
    setActiveXAxisIndex,
  ] = useState<number | null>(null);

  const [startIndex, setStartIndex] =
    useState(0);

  const [endIndex, setEndIndex] =
    useState(
      Math.max(
        0,
        (data?.trend?.length ?? 1) - 1,
      ),
    );

  /* ------------------------------------------------------------------------ */
  /*                            CHART DATA                                     */
  /* ------------------------------------------------------------------------ */

  const chartData = useMemo(() => {
    return (data?.trend ?? []).map(
      (item) => ({
        /*
         * IMPORTANT:
         * Display both day + date.
         *
         * Monday + 2026-08-17
         *       ↓
         * Mon 17
         */
        day: getDayDateLabel(
          item.dayName,
          item.workDate,
        ),

        me: Number(
          item.meActiveHours ?? 0,
        ),

        teamAvg: Number(
          item.teamAvgActiveHours ?? 0,
        ),

        teamBest: Number(
          item.teamBestActiveHours ?? 0,
        ),

        employeeName:
          data?.manager
            ?.employeeName ?? '',

        managerName:
          data?.manager
            ?.managerName ?? '',

        /*
         * Keep original API fields available.
         */
        dayName: item.dayName,

        workDate: item.workDate,
      }),
    );
  }, [data]);

  /* ------------------------------------------------------------------------ */
  /*                     DYNAMIC Y-AXIS CALCULATION                            */
  /* ------------------------------------------------------------------------ */

  const yAxisMax = useMemo(() => {
    if (!chartData.length) {
      return 1;
    }

    const allValues =
      chartData.flatMap(
        (item) => [
          item.me,
          item.teamAvg,
          item.teamBest,
        ],
      );

    const validValues =
      allValues.filter(
        (value) =>
          Number.isFinite(value),
      );

    if (!validValues.length) {
      return 1;
    }

    const maxValue =
      Math.max(...validValues);

    return getNiceYAxisMax(
      maxValue,
    );
  }, [chartData]);

  /* ------------------------------------------------------------------------ */
  /*                              Y AXIS TICKS                                */
  /* ------------------------------------------------------------------------ */

  const yAxisTicks = useMemo(() => {
    return getYAxisTicks(
      yAxisMax,
    );
  }, [yAxisMax]);

  /* ------------------------------------------------------------------------ */
  /*                       RESET ZOOM WHEN DATA CHANGES                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    setStartIndex(0);

    setEndIndex(
      Math.max(
        0,
        chartData.length - 1,
      ),
    );

    setActiveXAxisIndex(null);
  }, [chartData.length]);

  /* ------------------------------------------------------------------------ */
  /*                          VISIBLE DATA                                     */
  /* ------------------------------------------------------------------------ */

  const visibleData =
    useMemo(() => {
      if (!chartData.length) {
        return [];
      }

      return chartData.slice(
        startIndex,
        endIndex + 1,
      );
    }, [
      chartData,
      startIndex,
      endIndex,
    ]);

  /* ------------------------------------------------------------------------ */
  /*                     CHART-ONLY MOUSE WHEEL                               */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const element =
      chartWheelRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {

      /*
       * Stop browser/page scrolling
       * while mouse is inside chart.
       */
      event.preventDefault();
      event.stopPropagation();

      /* -------------------------------------------------------------- */
      /* SCROLL UP = ZOOM IN                                            */
      /* -------------------------------------------------------------- */

      if (event.deltaY < 0) {

        setStartIndex(
          (previousStart) =>
            Math.min(
              previousStart + 1,
              Math.max(
                0,
                chartData.length -
                  MIN_VISIBLE_POINTS,
              ),
            ),
        );

        setEndIndex(
          (previousEnd) =>
            Math.max(
              previousEnd - 1,
              MIN_VISIBLE_POINTS - 1,
            ),
        );

        return;
      }

      /* -------------------------------------------------------------- */
      /* SCROLL DOWN = ZOOM OUT                                         */
      /* -------------------------------------------------------------- */

      if (event.deltaY > 0) {

        setStartIndex(
          (previousStart) =>
            Math.max(
              0,
              previousStart - 1,
            ),
        );

        setEndIndex(
          (previousEnd) =>
            Math.min(
              chartData.length - 1,
              previousEnd + 1,
            ),
        );
      }
    };

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [chartData.length]);

  /* ------------------------------------------------------------------------ */
  /*                              EMPTY STATE                                 */
  /* ------------------------------------------------------------------------ */

  if (
    !data ||
    !chartData.length
  ) {
    return (
      <div
        className="
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          Me vs Team · Active Hours
        </h3>

        <div
          className="
            flex
            h-[240px]
            items-center
            justify-center
            text-[12px]
            text-[#667085]
          "
        >
          No data available
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /*                              RENDER                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="
        w-full
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >

      {/* ------------------------------------------------------------------ */}
      {/* HEADER                                                             */}
      {/* ------------------------------------------------------------------ */}

      <div
        className="
          mb-5
          flex
          flex-wrap
          items-center
          justify-between
          gap-2
        "
      >
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          Me vs Team · Active Hours
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-1
            text-[10px]
            font-medium
            tracking-wide
            text-[#2563EB]
          "
        >
          Daily active hours this week ·{' '}
          {data.manager.managerName}
        </span>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* LEGEND                                                             */}
      {/* ------------------------------------------------------------------ */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          justify-center
          gap-6
          text-[13px]
          font-medium
        "
      >

        {/* Me */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setActiveLine('me')
          }
          onMouseLeave={() =>
            setActiveLine(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#7C4DDF]
            "
          />

          <span
            className={
              activeLine === 'me'
                ? 'font-semibold text-[#7C4DDF]'
                : 'text-[#7C4DDF]'
            }
          >
            Me (
            {data.manager.employeeName}
            )
          </span>
        </div>

        {/* Team Avg */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setActiveLine('teamAvg')
          }
          onMouseLeave={() =>
            setActiveLine(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#94A3B8]
            "
          />

          <span
            className={
              activeLine === 'teamAvg'
                ? 'font-semibold text-[#334155]'
                : 'text-[#64748B]'
            }
          >
            Team Avg
          </span>
        </div>

        {/* Team Best */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setActiveLine('teamBest')
          }
          onMouseLeave={() =>
            setActiveLine(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#22C55E]
            "
          />

          <span
            className={
              activeLine === 'teamBest'
                ? 'font-semibold text-[#16A34A]'
                : 'text-[#22C55E]'
            }
          >
            Team Best
          </span>
        </div>

      </div>

      {/* ------------------------------------------------------------------ */}
      {/* CHART                                                              */}
      {/* ------------------------------------------------------------------ */}

      <div
        ref={chartWheelRef}
        className="
          relative
          w-full
          overflow-hidden
          rounded-[10px]
        "
        style={{
          overscrollBehavior:
            'contain',
        }}
      >

        <ResponsiveContainer
          width="100%"
          height={240}
        >
          <LineChart
            data={visibleData}
            margin={{
              top: 30,
              right: 20,
              left: 0,
              bottom: 10,
            }}
            syncId="activeHours"
            onMouseMove={(state: any) => {
              const index =
                state?.activeTooltipIndex;

              if (
                index !== undefined &&
                index !== null &&
                Number.isFinite(
                  Number(index),
                )
              ) {
                setActiveXAxisIndex(
                  Number(index),
                );
              }
            }}
            onMouseLeave={() => {
              setActiveXAxisIndex(null);
            }}
          >

            {/* Grid */}

            <CartesianGrid
              stroke="#E5E7EB"
              strokeDasharray="0"
              vertical={false}
            />

            {/* X Axis */}

            <XAxis
              dataKey="day"
              interval={0}
              height={58}
              tickMargin={0}
              axisLine={{
                stroke: '#D0D5DD',
              }}
              tickLine={false}
              tick={
                <CustomXAxisTick
                  activeIndex={
                    activeXAxisIndex
                  }
                />
              }
            />

            {/* Dynamic Y Axis */}

            <YAxis
              domain={[
                0,
                yAxisMax,
              ]}
              ticks={yAxisTicks}
              allowDecimals={false}
              tickFormatter={(value) =>
                `${value}h`
              }
              axisLine={false}
              tickLine={false}
              width={42}
              tick={{
                fontSize: 12,
                fill: '#475569',
              }}
            />

            {/* Tooltip */}

            <Tooltip
              content={
                <CustomTooltip />
              }
              cursor={{
                stroke: '#7C4DDF',
                strokeWidth: 1.5,
                strokeDasharray:
                  '4 4',
              }}
              wrapperStyle={{
                outline: 'none',
              }}
            />

            {/* ---------------------------------------------------------- */}
            {/* ME                                                           */}
            {/* ---------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="me"
              name={`Me (${data.manager.employeeName})`}
              stroke="#7C4DDF"
              strokeWidth={
                activeLine === 'me'
                  ? 4
                  : 3
              }
              strokeOpacity={
                activeLine &&
                activeLine !== 'me'
                  ? 0.2
                  : 1
              }
              dot={{
                r: 4,
                fill: '#7C4DDF',
                strokeWidth: 0,
              }}
              activeDot={{
                r: 8,
                fill: '#7C4DDF',
                stroke: '#FFFFFF',
                strokeWidth: 3,
              }}
              onMouseEnter={() =>
                setActiveLine('me')
              }
              onMouseLeave={() =>
                setActiveLine(null)
              }
              isAnimationActive={false}
            />

            {/* ---------------------------------------------------------- */}
            {/* TEAM AVG                                                     */}
            {/* ---------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="teamAvg"
              name="Team Avg"
              stroke="#94A3B8"
              strokeWidth={
                activeLine === 'teamAvg'
                  ? 3
                  : 2
              }
              strokeDasharray="5 5"
              strokeOpacity={
                activeLine &&
                activeLine !== 'teamAvg'
                  ? 0.2
                  : 1
              }
              dot={false}
              onMouseEnter={() =>
                setActiveLine('teamAvg')
              }
              onMouseLeave={() =>
                setActiveLine(null)
              }
              isAnimationActive={false}
            />

            {/* ---------------------------------------------------------- */}
            {/* TEAM BEST                                                    */}
            {/* ---------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="teamBest"
              name="Team Best"
              stroke="#22C55E"
              strokeWidth={
                activeLine === 'teamBest'
                  ? 3
                  : 2
              }
              strokeDasharray="5 5"
              strokeOpacity={
                activeLine &&
                activeLine !== 'teamBest'
                  ? 0.2
                  : 1
              }
              dot={false}
              onMouseEnter={() =>
                setActiveLine('teamBest')
              }
              onMouseLeave={() =>
                setActiveLine(null)
              }
              isAnimationActive={false}
            />

          </LineChart>
        </ResponsiveContainer>

        {/* ---------------------------------------------------------------- */}
        {/* Y AXIS INFO                                                      */}
        {/* ---------------------------------------------------------------- */}

        <div
          className="
            pointer-events-none
            absolute
            left-[46px]
            top-1
            rounded-full
            bg-white/90
            px-2
            py-1
            text-[9px]
            text-[#667085]
            shadow-sm
          "
        >
          Max {yAxisMax}h
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* ZOOM HINT                                                        */}
        {/* ---------------------------------------------------------------- */}

        {/* <div
          className="
            pointer-events-none
            absolute
            bottom-1
            right-3
            rounded-full
            bg-white/90
            px-2
            py-1
            text-[9px]
            text-[#667085]
            shadow-sm
          "
        >
          
          Scroll ↑ Zoom in · Scroll ↓ Zoom out
        </div> */}

      </div>
    </div>
  );
};

export default MeVsTeamActiveHours;